import React, { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Camera, Link as LinkIcon, Maximize2, Minimize2, Filter, Columns3, X, Eye, EyeOff } from "lucide-react";

const MONO = ["po_number", "vin", "front_cover_code", "rear_cover_code", "catalog_number", "f_clamp_code", "r_clamp_code", "tracking"];
const HIDDEN_KEY = "crm-report-hidden-cols";

const loadHidden = () => { try { return JSON.parse(localStorage.getItem(HIDDEN_KEY) || "[]"); } catch { return []; } };

/** Show / hide columns popover (remembered per browser). */
function ColumnsMenu({ cols, hidden, setHidden }) {
  const toggle = (k) => setHidden((h) => (h.includes(k) ? h.filter((x) => x !== k) : [...h, k]));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-[11px] font-medium transition-colors ${hidden.length ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"}`} data-testid="rpt-columns-btn" title="Show / hide columns">
          <Columns3 size={13} /> Columns{hidden.length ? ` · ${hidden.length} hidden` : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2" data-testid="rpt-columns-menu">
        <div className="flex items-center justify-between mb-1.5 px-1">
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Columns</span>
          <div className="flex gap-2 text-[11px]">
            <button type="button" className="underline text-slate-500 hover:text-slate-900" onClick={() => setHidden([])} data-testid="rpt-columns-show-all">show all</button>
            <button type="button" className="underline text-slate-500 hover:text-slate-900" onClick={() => setHidden(cols.map(([k]) => k))} data-testid="rpt-columns-hide-all">hide all</button>
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto space-y-0.5">
          {cols.map(([k, h]) => {
            const on = !hidden.includes(k);
            return (
              <label key={k} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-slate-50 cursor-pointer text-xs">
                <input type="checkbox" checked={on} onChange={() => toggle(k)} data-testid={`rpt-col-toggle-${k}`} />
                {on ? <Eye size={12} className="text-slate-500" /> : <EyeOff size={12} className="text-slate-300" />}
                <span className={on ? "" : "text-slate-400 line-through"}>{h}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Excel-style result grid with: ⛶ full-screen mode, per-column filter row, show/hide columns.
 * `rows` are the server rows; column filters are applied client-side on the displayed text.
 */
export function ReportGrid({ cols, rows, loading, count, fmt, lastRow, gridRef, onOpenOrder, onPeek, summary }) {
  const [full, setFull] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [colFilters, setColFilters] = useState({});
  const [hidden, setHidden] = useState(loadHidden);
  useEffect(() => { try { localStorage.setItem(HIDDEN_KEY, JSON.stringify(hidden)); } catch { /* ignore */ } }, [hidden]);
  useEffect(() => {
    if (!full) return;
    const onKey = (e) => e.key === "Escape" && setFull(false);
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [full]);

  const visible = useMemo(() => cols.filter(([k]) => !hidden.includes(k)), [cols, hidden]);
  const activeFilters = Object.entries(colFilters).filter(([, v]) => v && v.trim());
  const shown = useMemo(() => {
    if (!activeFilters.length) return rows;
    const needles = activeFilters.map(([k, v]) => [k, v.trim().toLowerCase()]);
    return rows.filter((r) => needles.every(([k, n]) => String(fmt(k, r[k]) || "").toLowerCase().includes(n)));
  }, [rows, activeFilters, fmt]);

  const wrap = full
    ? "fixed inset-0 z-[80] bg-white flex flex-col"
    : "rounded-lg border border-slate-300 bg-white overflow-hidden shadow-sm flex flex-col";
  return (
    <div className={wrap} data-testid="rpt-grid-wrap" data-fullscreen={full ? "1" : undefined}>
      {/* Grid toolbar */}
      <div className={`flex flex-wrap items-center gap-2 px-3 py-1.5 border-b border-slate-200 bg-slate-50 ${full ? "shadow-sm" : ""}`} data-testid="rpt-grid-toolbar">
        {full ? <div className="text-sm min-w-0 flex flex-wrap items-center gap-2" data-testid="rpt-grid-summary">{summary}</div>
          : <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Excel view</span>}
        {activeFilters.length > 0 && (
          <span className="text-[11px] text-slate-600" data-testid="rpt-grid-filtered">
            showing <b className="tabular-nums">{shown.length}</b> of {rows.length} rows
            <button type="button" className="ml-1.5 underline text-slate-500 hover:text-slate-900" onClick={() => setColFilters({})} data-testid="rpt-colfilters-clear">clear column filters</button>
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          <button type="button" onClick={() => setShowFilters((v) => !v)} className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-[11px] font-medium transition-colors ${showFilters || activeFilters.length ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"}`} data-testid="rpt-colfilters-btn" title="Filter inside each column" aria-pressed={showFilters}>
            <Filter size={13} /> Column filters{activeFilters.length ? ` · ${activeFilters.length}` : ""}
          </button>
          <ColumnsMenu cols={cols} hidden={hidden} setHidden={setHidden} />
          <button type="button" onClick={() => setFull((v) => !v)} className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-slate-300 bg-white text-[11px] font-medium text-slate-700 hover:border-slate-500 transition-colors" data-testid="rpt-fullscreen-btn" title={full ? "Exit full screen (Esc)" : "Full screen"}>
            {full ? <><Minimize2 size={13} /> Exit full screen</> : <><Maximize2 size={13} /> Full screen</>}
          </button>
        </div>
      </div>

      <div className={`overflow-auto ${full ? "flex-1" : "max-h-[62vh]"}`} data-testid="rpt-grid-scroll" ref={gridRef}>
        <table className="text-[11px] border-collapse" style={{ minWidth: "max-content" }} data-testid="rpt-table">
          <thead className="sticky top-0 z-20">
            <tr className="bg-slate-100 text-slate-700 group">
              <th className="sticky left-0 z-30 bg-slate-100 border border-slate-300 px-2 py-1.5 text-center font-semibold w-10">#</th>
              <th className="sticky left-10 z-30 bg-slate-100 border border-slate-300 px-2 py-1.5 text-center font-semibold" style={{ minWidth: 60 }}>📷</th>
              {visible.map(([k, h, w]) => (
                <th key={k} className="border border-slate-300 px-2 py-1.5 text-left font-semibold whitespace-nowrap" style={{ minWidth: w }} data-testid={`rpt-th-${k}`}>
                  <span className="inline-flex items-center gap-1">
                    {h}
                    <button type="button" onClick={() => setHidden((hh) => [...hh, k])} className="opacity-0 group-hover:opacity-60 hover:!opacity-100 focus:opacity-100 text-slate-400 hover:text-slate-900" title={`Hide ${h}`} data-testid={`rpt-th-hide-${k}`}><X size={10} /></button>
                  </span>
                </th>
              ))}
            </tr>
            {showFilters && (
              <tr className="bg-white" data-testid="rpt-colfilters-row">
                <th className="sticky left-0 z-30 bg-white border border-slate-200 px-1 py-1 text-center"><Filter size={10} className="inline text-slate-400" /></th>
                <th className="sticky left-10 z-30 bg-white border border-slate-200 px-1 py-1" />
                {visible.map(([k, h]) => (
                  <th key={k} className="border border-slate-200 px-1 py-1">
                    <Input value={colFilters[k] || ""} onChange={(e) => setColFilters((c) => ({ ...c, [k]: e.target.value }))} placeholder={`filter ${h.toLowerCase()}`} className={`h-6 text-[11px] md:text-[11px] px-1.5 font-normal ${colFilters[k] ? "border-slate-900" : ""}`} data-testid={`rpt-colfilter-${k}`} />
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {shown.map((r, i) => {
              const isLast = lastRow && r.id === lastRow;
              const rowBg = isLast ? "bg-amber-100" : r.is_archive ? (i % 2 ? "bg-emerald-50/50" : "bg-emerald-50/25") : (i % 2 ? "bg-slate-50/60" : "bg-white");
              const stickyBg = isLast ? "bg-amber-100" : r.is_archive ? "bg-emerald-50" : (i % 2 ? "bg-slate-50" : "bg-white");
              return (
                <tr
                  key={r.id}
                  className={`${rowBg} ${r.is_archive ? "hover:bg-emerald-100/60" : "hover:bg-amber-50 cursor-pointer"} ${isLast ? "ring-2 ring-inset ring-amber-400" : ""}`}
                  onClick={r.is_archive ? undefined : () => onOpenOrder(r.id)}
                  title={r.is_archive ? `Old worksheet “${r.archive_name}” · sheet row ${r.row_no}` : undefined}
                  data-testid={`rpt-row-${r.id}`}
                  data-archive={r.is_archive ? "1" : undefined}
                >
                  <td className={`sticky left-0 z-10 border border-slate-200 px-2 py-1 text-center text-slate-400 tabular-nums ${stickyBg}`}>{i + 1}</td>
                  <td className={`sticky left-10 z-10 border border-slate-200 px-1 py-0.5 text-center ${stickyBg}`} onClick={(e) => e.stopPropagation()}>
                    {r.is_archive && !r.gallery_folder_id ? (
                      r.photo_url
                        ? <a href={r.photo_url} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-6 w-8 rounded border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors" title="Open photo / Drive link from the old sheet" data-testid={`rpt-photo-link-${r.id}`}><LinkIcon size={13} /></a>
                        : <span className="text-slate-300">—</span>
                    ) : r.has_photos ? (
                      <button type="button" onClick={() => onPeek(r)} className={`inline-flex items-center justify-center h-6 w-8 rounded border bg-white transition-colors ${r.is_archive ? "border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white" : "border-slate-300 text-slate-700 hover:bg-slate-900 hover:text-white"}`} title={r.is_archive ? `Gallery photos · ${r.gallery_folder_name}` : "Preview photos"} data-testid={`rpt-photos-${r.id}`}>
                        <Camera size={13} />
                      </button>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  {visible.map(([k]) => (
                    <td
                      key={k}
                      className={`border border-slate-200 px-2 py-1 whitespace-nowrap ${MONO.includes(k) ? "font-mono" : ""} ${k === "po_number" ? "font-semibold" : ""}`}
                      style={k === "po_number" && r.fit ? { background: "#f4a3dd" } : undefined}
                      title={k === "po_number" && r.fit ? "FIT" : undefined}
                      data-fit={k === "po_number" && r.fit ? "1" : undefined}
                    >
                      {k === "po_number" && r.is_archive ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Badge className="bg-emerald-600 text-white border-emerald-700 text-[9px] px-1 py-0 font-sans tracking-wide" data-testid={`rpt-old-badge-${r.id}`}>OLD SHEET</Badge>
                          {fmt(k, r[k])}
                        </span>
                      ) : fmt(k, r[k])}
                    </td>
                  ))}
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={visible.length + 2} className="px-3 py-10 text-center text-slate-400 border border-slate-200">No orders match these filters.</td></tr>
            )}
            {!loading && rows.length > 0 && shown.length === 0 && (
              <tr><td colSpan={visible.length + 2} className="px-3 py-10 text-center text-slate-400 border border-slate-200" data-testid="rpt-colfilters-empty">No rows match the column filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
